import { Dog, Beef, Shield, Sprout, Wheat, Flame, Tractor, Leaf, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import cardPetFood from "@/assets/card-pet-food.jpg";
import cardLivestock from "@/assets/card-livestock.jpg";
import cardPlantProtection from "@/assets/fitosanitari.jpg";
import cardFertilizer from "@/assets/card-fertilizer.jpg";
import cardSeeds from "@/assets/card-seeds.jpg";
import cardFlour from "@/assets/card-flour.jpg";
import cardAgriculture from "@/assets/card-agriculture.jpg";
import cardPellet from "@/assets/pellet-banner.jpg";

const ProductCategories = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const categories = [
    {
      icon: Dog,
      title: "Alimentazione Animali Domestici",
      link: "/prodotti/alimentazione-animali",
      backgroundImage: cardPetFood,
      items: [
        "Crocchette per cani di tutte le taglie",
        "Cibo secco e umido per gatti",
        "Snack e integratori",
        "Marchi: Prolife, Trainer, Libra, Disugual, Schesir",
      ],
    },
    {
      icon: Beef,
      title: "Mangimi per Allevamento",
      link: "/prodotti/mangimi-allevamento",
      backgroundImage: cardLivestock,
      items: [
        "Mangimi per bovini, suini, ovini",
        "Alimenti per pollame e conigli",
        "Integratori e sali minerali",
        "Mangimi biologici",
      ],
    },
    {
      icon: Shield,
      title: "Prodotti Fitosanitari",
      link: "/prodotti/fitosanitari",
      backgroundImage: cardPlantProtection,
      items: [
        "Veleni per topi e insetti",
        "Disinfestanti",
        "Repellenti",
        "Protezione delle colture",
      ],
    },
    {
      icon: Sprout,
      title: "Concimi e Terricci",
      link: "/prodotti/concimi-terricci",
      backgroundImage: cardFertilizer,
      items: [
        "Concimi organici e chimici",
        "Terricci per orto e giardino",
        "Ammendanti e substrati",
        "Prodotti biologici certificati",
      ],
    },
    {
      icon: Wheat,
      title: "Sementi e Piantine",
      link: "/prodotti/sementi-piantine",
      backgroundImage: cardSeeds,
      items: [
        "Sementi per orto e prato",
        "Piantine da orto stagionali",
        "Bulbi e tuberi stagionali",
      ],
    },
    {
      icon: Leaf,
      title: "Farine e Cereali",
      link: "/prodotti/farine",
      backgroundImage: cardFlour,
      items: [
        "Farine macinate a pietra",
        "Farine biologiche",
        "Semole e cereali",
        "Prodotti del territorio",
      ],
    },
    {
      icon: Tractor,
      title: "Attrezzatura Agricola",
      link: "/prodotti/attrezzatura-agricola",
      backgroundImage: cardAgriculture,
      items: [
        "Attrezzi manuali",
        "Irrigazione",
        "Accessori per la coltivazione",
      ],
    },
    {
      icon: Flame,
      title: "Pellet e Combustibili",
      link: "/prodotti/pellet-combustibili",
      backgroundImage: cardPellet,
      items: [
        "Pellet per stufe",
        "Pellet certificato A1",
        "Consegna a domicilio disponibile",
      ],
    },
  ];

  const scrollToCard = (index: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const cards = container.querySelectorAll('a');
    const targetCard = cards[index];
    if (!targetCard) return;

    const containerRect = container.getBoundingClientRect();
    const cardRect = targetCard.getBoundingClientRect();

    const scrollLeft = container.scrollLeft + cardRect.left - containerRect.left - (containerRect.width / 2 - cardRect.width / 2);

    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    // Centra la prima card all'avvio
    const timer = setTimeout(() => {
      scrollToCard(0);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const goToPrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      scrollToCard(newIndex);
    }
  };

  const goToNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (currentIndex < categories.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      scrollToCard(newIndex);
    }
  };

  return (
    <section id="prodotti" className="py-6 md:py-12 section-alt">
      <div className="container mx-auto px-4">
        <div className="text-center mb-8 md:mb-16 animate-fade-in-up">
          <h2 className="mb-4 text-primary uppercase text-2xl md:text-4xl text-center" style={{ fontFamily: 'Verdana, sans-serif' }}>I nostri prodotti</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto" style={{ fontFamily: 'Verdana, sans-serif' }}>
            Un'ampia gamma di prodotti selezionati per soddisfare ogni esigenza
          </p>
        </div>

        {/* Desktop Grid */}
        <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((category, index) => (
            <Link
              key={index}
              to={category.link}
              className="relative rounded-2xl p-6 card-hover animate-fade-in-up border border-border block transition-transform hover:scale-105 overflow-hidden group"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Background Image */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-300 group-hover:scale-110"
                style={{ backgroundImage: `url(${category.backgroundImage})` }}
              />
              {/* Overlay for readability */}
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/35 to-black/50" />
              
              {/* Content */}
              <div className="relative z-10">
                <div className="icon-wrapper mb-4 text-white">
                  <category.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-white mb-4">
                  {category.title}
                </h3>
                <ul className="space-y-2">
                  {category.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="text-white/90 flex items-start">
                      <span className="text-white mr-2">•</span>
                      <span className="text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Link>
          ))}
        </div>

        {/* Mobile Horizontal Scroll */}
        <div className="sm:hidden relative">
          {/* Left Arrow */}
          {currentIndex > 0 && (
            <button
              onClick={goToPrevious}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-50 bg-primary text-white w-10 h-10 rounded-full flex items-center justify-center shadow-lg hover:bg-primary/90 transition-all"
              aria-label="Scorri a sinistra"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
          )}

          {/* Right Arrow */}
          {currentIndex < categories.length - 1 && (
            <button
              onClick={goToNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-50 bg-primary text-white w-10 h-10 rounded-full flex items-center justify-center shadow-lg hover:bg-primary/90 transition-all"
              aria-label="Scorri a destra"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          )}

          <div
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {categories.map((category, index) => (
              <Link
                key={index}
                to={category.link}
                className="relative rounded-2xl p-6 border border-border block min-w-[280px] flex-shrink-0 overflow-hidden"
              >
                {/* Background Image */}
                <div 
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${category.backgroundImage})` }}
                />
                {/* Overlay for readability */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/35 to-black/50" />
                
                {/* Content */}
                <div className="relative z-10">
                  <div className="icon-wrapper mb-4 text-white">
                    <category.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-white mb-4">
                    {category.title}
                  </h3>
                  <ul className="space-y-2">
                    {category.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="text-white/90 flex items-start">
                        <span className="text-white mr-2">•</span>
                        <span className="text-sm">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductCategories;
